import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import Autocomplete from '@mui/joy/Autocomplete';
import CircularProgress from '@mui/joy/CircularProgress';
import Button from '@mui/joy/Button';
import _ from 'lodash';
import { listSettlementGroups } from '../API/settlementGroup';
import { getPaginatedResults } from '../utils/pagination';
import { PERMISSIONS } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

function labelForGroup(option) {
  if (!option) return '';
  const name = option.name || '';
  const code = option.code ? String(option.code).trim() : '';
  const sep = option.separate_checkout ? ' · separate' : '';
  return code ? `${name} (${code})${sep}` : name || `#${option.id}`;
}

/**
 * Autocomplete for SettlementGroup master rows (pricing rules + manual lines).
 */
export default function SettlementGroupOptionList({
  onChange,
  required,
  defaultValue,
  disabled = false,
  label,
  errorMessage,
  activeOnly = true,
}) {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const canList = hasPermission(PERMISSIONS.SETTLEMENT_GROUPS_LIST);
  const resolvedLabel = label ?? t('pricingRules.fieldSettlementGroup');
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchGroups = async (search) => {
    if (!canList) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = {
        page: 1,
        page_size: 50,
        search: search || '',
        ordering: 'sort_order',
      };
      if (activeOnly) params.active = true;
      const response = await listSettlementGroups(params);
      setOptions(getPaginatedResults(response?.data));
    } catch (err) {
      console.error('SettlementGroupOptionList:', err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(_.debounce(fetchGroups, 300), [canList, activeOnly]);

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    if (!newInputValue) {
      fetchGroups('');
    }
  };

  useEffect(() => {
    if (inputValue) {
      debouncedFetch(inputValue);
    }
  }, [inputValue, debouncedFetch]);

  useEffect(() => {
    if (open) {
      fetchGroups('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Box
      sx={{
        marginBottom: 2,
        position: 'relative',
        '&:focus-within': {
          boxShadow: '0 0 8px rgba(0, 128, 255, 0.3)',
          borderRadius: '8px',
        },
      }}
    >
      <FormLabel
        sx={{
          fontWeight: 600,
          color: '#555',
          marginBottom: '6px',
          fontSize: '14px',
        }}
      >
        {required ? `${resolvedLabel} *` : resolvedLabel}
      </FormLabel>
      <Autocomplete
        onInputChange={handleInputChange}
        value={defaultValue == null || defaultValue === '' ? null : defaultValue}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        placeholder={t('widgets.searchSettlementGroups', {
          defaultValue: 'Search settlement groups…',
        })}
        options={Array.isArray(options) ? options : []}
        loading={loading}
        disabled={disabled || !canList}
        onChange={onChange}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        getOptionLabel={(option) => labelForGroup(option)}
        noOptionsText={
          !canList ? (
            <span>
              {t('permissions.noPermission', {
                defaultValue: 'You do not have permission to perform this action.',
              })}
            </span>
          ) : (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              px={2}
            >
              <span>{t('widgets.typeToSearch')}</span>
              <Button
                variant="solid"
                color="primary"
                size="sm"
                onClick={() => window.open('/settings/settlementgroups', '_blank')}
              >
                {t('widgets.manageSettlementGroups', {
                  defaultValue: 'Manage groups',
                })}
              </Button>
            </Box>
          )
        }
        loadingText={t('actions.loading')}
        filterOptions={(opts) => opts}
        endDecorator={
          loading && (
            <CircularProgress size="sm" sx={{ bgcolor: 'background.surface' }} />
          )
        }
        sx={{
          backgroundColor: '#f7f9fc',
          borderRadius: '8px',
          padding: '4px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            borderColor: '#5f09c2',
          },
          '&:focus': {
            borderColor: '#5f09c2',
            outline: 'none',
            boxShadow: '0 0 6px rgba(0, 123, 255, 0.5)',
          },
          '&[disabled]': {
            backgroundColor: '#e9ecef',
            color: '#6c757d',
            cursor: 'not-allowed',
          },
          ...(errorMessage
            ? {
                borderColor: 'var(--joy-palette-danger-500, #c41c1c)',
                boxShadow: '0 0 0 1px var(--joy-palette-danger-500, #c41c1c)',
              }
            : {}),
        }}
      />
      {errorMessage ? (
        <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>{errorMessage}</FormHelperText>
      ) : null}
    </Box>
  );
}
